/**
 * CLI telemetry — opt-out PostHog analytics.
 *
 * Captures CLI usage events to measure adoption, command popularity,
 * error rates, and onboarding friction. Events are sent directly to
 * PostHog's HTTP capture API (fire-and-forget, never blocks CLI).
 *
 * Opt-out: set ELECTRIC_TELEMETRY=off to disable all telemetry.
 * No PII is captured (no database URLs, no token values, no resource names).
 */

import { readFile, writeFile, mkdir } from 'node:fs/promises'
import { join } from 'node:path'
import { randomUUID } from 'node:crypto'
import { getConfigDir } from './config.js'

const POSTHOG_HOST = `https://us.i.posthog.com`

declare const __POSTHOG_KEY__: string
const POSTHOG_KEY =
  typeof __POSTHOG_KEY__ !== `undefined` && __POSTHOG_KEY__
    ? __POSTHOG_KEY__
    : ``

export function isTelemetryEnabled(): boolean {
  if (process.env.ELECTRIC_TELEMETRY?.toLowerCase() === `off`) return false
  if (!POSTHOG_KEY) return false
  return true
}

function getAnonymousIdFile(): string {
  return join(getConfigDir(), `anonymous-id`)
}

let cachedAnonymousId: string | undefined

async function getOrCreateAnonymousId(): Promise<string> {
  if (cachedAnonymousId) return cachedAnonymousId

  const idFile = getAnonymousIdFile()
  try {
    const id = (await readFile(idFile, `utf-8`)).trim()
    if (id) {
      cachedAnonymousId = id
      return id
    }
  } catch {
    // File doesn't exist yet
  }

  const id = randomUUID()
  try {
    const configDir = getConfigDir()
    await mkdir(configDir, { recursive: true })
    await writeFile(idFile, id, { mode: 0o600 })
  } catch {
    // Best-effort persistence
  }
  cachedAnonymousId = id
  return id
}

interface TelemetryEvent {
  event: string
  properties?: Record<string, unknown>
  distinctId?: string
}

/**
 * Capture a telemetry event. Fire-and-forget — never throws, never blocks.
 */
export function capture(telemetryEvent: TelemetryEvent): void {
  if (!isTelemetryEnabled()) return

  // Fire and forget — track for flush
  const promise = captureAsync(telemetryEvent)
  pendingRequests.push(promise)
  promise.finally(() => {
    const idx = pendingRequests.indexOf(promise)
    if (idx >= 0) pendingRequests.splice(idx, 1)
  })
}

async function captureAsync(telemetryEvent: TelemetryEvent): Promise<void> {
  try {
    const distinctId =
      telemetryEvent.distinctId ?? (await getOrCreateAnonymousId())
    const payload = {
      api_key: POSTHOG_KEY,
      event: telemetryEvent.event,
      properties: {
        ...telemetryEvent.properties,
        $lib: `electric-cli`,
      },
      distinct_id: distinctId,
      timestamp: new Date().toISOString(),
    }

    await fetch(`${POSTHOG_HOST}/i/v0/e/`, {
      method: `POST`,
      headers: { 'Content-Type': `application/json` },
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(5000),
    })
  } catch {
    // Silently swallow — telemetry must never affect CLI operation
  }
}

const pendingRequests: Promise<void>[] = []

/**
 * Flush any pending telemetry. Call before process exit to give
 * fire-and-forget requests a chance to complete.
 */
export async function flush(): Promise<void> {
  await Promise.allSettled(pendingRequests)
  pendingRequests.length = 0
}

/** @internal — for testing only */
export function _resetCachedAnonymousId(): void {
  cachedAnonymousId = undefined
}
